<template>
  <input
    v-if="!mode"
    :value="value"
    :type="type"
    :disabled="disabled"
    :class="$b({ hasError, disabled })"
    v-on="{
      keydown: handleKeyDown,
      input: handleInput,
      blur: () => $emit('blur'),
      focus: () => $emit('focus')
    }"
  >
  <div
    v-else
    :class="$b({ mode, border, hasError, shape, grow, disabled })"
  >
    <div
      v-if="$slots.prepend"
      :class="$b('prepend')"
    >
      <slot name="prepend" />
    </div>

    <input
      :value="value"
      :type="type"
      :class="[$b('input', {}), 't-body']"
      :placeholder="placeholder"
      :disabled="disabled"
      v-on="{
        keydown: handleKeyDown,
        input: handleInput,
        blur: () => $emit('blur'),
        focus: () => $emit('focus')
      }"
    >
    <div
      v-if="$slots.default"
      :class="$b('append')"
    >
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextInput',

  props: {
    modelValue: {
      type: [Number, String],
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: value => [
        'text',
        'email',
        'search',
        'tel',
        'number'
      ].includes(value)
    },
    mode: {
      type: String,
      default: '',
      validator: value => [
        '',
        'white',
        'grey',
        'promo'
      ].includes(value)
    },
    integerOnly: {
      type: Boolean,
      default: false
    },
    border: {
      type: String,
      validator: value => [
        'grey',
        'blue'
      ].includes(value),
      default: 'grey',
      required: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    shape: {
      type: String,
      default: 'square',
      validator: (value) => {
        return [
          'rounded',
          'square'
        ].includes(value)
      }
    },
    placeholder: {
      type: String,
      default: ''
    },
    grow: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue', 'blur', 'focus'],

  computed: {
    value: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    }
  },

  methods: {
    handleInput (event) {
      let value = event.target.value

      if (this.type === 'number' && this.integerOnly) value = Math.round(value)

      this.value = value
    },
    handleKeyDown (event) {
      if (!this.type === 'number') return

      // Prevent dot (.) if we're only accepting integer values
      if (event.keyCode === 110 || event.keyCode === 190) {
        if (this.integerOnly) {
          event.preventDefault()
        }
      }
    }
  }
}
</script>

<style lang="postcss">
.TextInput--grow {
  @apply w-full;
}

.TextInput__input {
  @screen mdMax {
    font-size: 16px;
  }

  .TextInput--grow & {
    @apply w-full;
  }
}

.TextInput--mode-promo {
  @apply
    bg-[var(--promo-b)]
    rounded-md
    inline-flex
    items-center
    transition
    promo-gradient-border-single promo-gradient-border-animated;
  
  &[disabled],
  &.TextInput--disabled {
    opacity: 0.5;
  }

  &:focus-within {
    border-color: theme('colors.grey.DEFAULT');

    .TextInput__append {
      transform: scale(1.05);
    }
  }

  &.TextInput--has-error {
    @apply border-red;
  }

  .TextInput__input {
    @apply p-xxs bg-transparent w-full;

    &::placeholder {
      overflow: visible;
    }
  }

  .TextInput__append {
    @apply m-xxs transition;
  }
}

.TextInput--mode-grey {
  @apply
    bg-grey-light
    rounded-md
    inline-flex
    items-center
    transition;

  border: 1px solid theme('colors.grey-light');

  &[disabled],
  &.TextInput--disabled {
    opacity: 0.5;
  }

  &:focus-within {
    border-color: theme('colors.grey.DEFAULT');

    .TextInput__append {
      transform: scale(1.05);
    }
  }

  &.TextInput--has-error {
    @apply border-red;
  }

  .TextInput__input {
    @apply p-xxs bg-transparent w-full;

    &::placeholder {
      overflow: visible;
    }
  }

  .TextInput__append {
    @apply m-xxs transition;
  }
}

.TextInput--mode-white {
  @apply
    bg-white
    rounded-md
    inline-flex
    items-center
    border border-grey-500;

  .is-dark-chrome-theme & {
    @apply bg-transparent;
  }

  &[disabled],
  &.TextInput--disabled {
    opacity: 0.5;
  }

  &:focus-within {
    @apply outline outline-2 outline-offset-2 outline-purple
      border-transparent;

    .TextInput__append {
      transform: scale(1.3);
    }
  }

  &.TextInput--has-error {
    @apply border-red;
  }

  .TextInput__input {
    @apply p-xxs bg-transparent;

    &::placeholder {
      overflow: visible;
    }
  }

  .TextInput__append {
    @apply m-xxs transition;
  }
}

.TextInput--border-blue {
  &:focus-within {
    border-color: theme('colors.legacy-blue');
  }
}

.TextInput--shape-rounded {
  @apply rounded-full;

  .TextInput__input {
    @apply ml-xs;
  }

  .TextInput__append {
    @apply mr-xs;
  }
}

.TextInput__prepend {
  @apply pl-sm -mr-xxs;
}
</style>
